import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

/**
 * True once the column's content has scrolled under the header (the iOS
 * scroll-edge hairline). Scroll events don't bubble, so this listens in the
 * capture phase on the header's column: the nearest ancestor holding more
 * than the header itself, whichever list or editor scrolls inside it.
 */
export function useContentScrolledUnder(ref: React.RefObject<HTMLElement | null>) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let scope = ref.current?.parentElement;
    while (scope && scope.childElementCount === 1) scope = scope.parentElement;
    if (!scope) return;
    const onScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      // Ignore horizontal scrollers (tag strips, tables) and the header itself.
      if (target.scrollHeight <= target.clientHeight || ref.current?.contains(target)) return;
      setScrolled(target.scrollTop > 0);
    };
    scope.addEventListener("scroll", onScroll, true);
    return () => scope.removeEventListener("scroll", onScroll, true);
  }, [ref]);
  return scrolled;
}

/** The hairline every header draws while content sits under it. */
export function scrollEdgeHairline(scrolled: boolean) {
  return cn("border-b transition-colors duration-150", scrolled ? "border-border-light" : "border-transparent");
}
